import { ChangeEvent, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Button } from '../../../../../../../components/atoms/button/button';
import { Input } from '../../../../../../../components/atoms/input/input';
import { Select } from '../../../../../../../components/atoms/select/select';
import { Textarea } from '../../../../../../../components/atoms/textarea/textarea';
import { Modal } from '../../../../../../../components/molecules/modal/modal';
import {
  AjusteAntecipacao,
  MoedaAjuste,
  MOEDA_AJUSTE_LABEL,
  MOEDA_AJUSTE_ORDER,
  UpsertAjusteAntecipacaoPayload,
} from '../../../../../../../types/ajuste-antecipacao';
import { formatValorMoeda } from './format';

interface Props {
  isOpen: boolean;
  existente?: AjusteAntecipacao | null;
  saving?: boolean;
  removing?: boolean;
  errorMessage?: string | null;
  onSubmit: (moeda: MoedaAjuste, payload: UpsertAjusteAntecipacaoPayload) => void;
  onRemove?: (moeda: MoedaAjuste) => void;
  onClose: () => void;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Info = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FieldError = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.danger};
`;

const ErrorAlert = styled.div`
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.dangerBg};
  border-left: 3px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  font-size: 13px;
`;

const ResetWarning = styled.div`
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.dangerBg};
  border-left: 3px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  font-size: 13px;
  line-height: 1.4;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const FooterRight = styled.div`
  display: flex;
  gap: 8px;
`;

interface Errors {
  valor?: string;
  motivo?: string;
}

const validate = (
  valor: string,
): Errors => {
  const errors: Errors = {};
  if (valor === '' || valor === null) {
    errors.valor = 'Valor é obrigatório';
  } else {
    const n = Number(valor);
    if (!Number.isFinite(n)) errors.valor = 'Valor inválido';
    else if (n < 0) errors.valor = 'Valor não pode ser negativo';
  }
  return errors;
};

export const AjusteAntecipacaoModal = ({
  isOpen,
  existente,
  saving,
  removing,
  errorMessage,
  onSubmit,
  onRemove,
  onClose,
}: Props) => {
  const [moeda, setMoeda] = useState<MoedaAjuste>(MoedaAjuste.BRL);
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (!isOpen) return;
    if (existente) {
      setMoeda(existente.moeda);
      setValor(String(existente.valor ?? 0));
      setMotivo(existente.motivo ?? '');
    } else {
      setMoeda(MoedaAjuste.BRL);
      setValor('0');
      setMotivo('');
    }
    setErrors({});
  }, [isOpen, existente]);

  const handleSubmit = () => {
    const next = validate(valor);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit(moeda, {
      valor: Number(valor),
      motivo: motivo.trim() || undefined,
    });
  };

  const handleRemove = () => {
    if (onRemove && existente) onRemove(existente.moeda);
  };

  const lockClose = saving || removing;

  return (
    <Modal
      isOpen={isOpen}
      onClose={lockClose ? () => undefined : onClose}
      title={existente ? 'Editar ajuste' : 'Registrar ajuste'}
      description="Controle interno de antecipação — não impacta saldo, saque, recebíveis ou reservas."
      size="md"
      footer={
        <Footer>
          {existente && onRemove ? (
            <Button
              variant="danger"
              onClick={handleRemove}
              loading={removing}
              disabled={saving}
              data-testid="ajuste-antecipacao-zerar"
            >
              Zerar
            </Button>
          ) : (
            <span />
          )}
          <FooterRight>
            <Button variant="ghost" onClick={onClose} disabled={lockClose}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={saving}
              disabled={removing}
              data-testid="ajuste-antecipacao-salvar"
            >
              Salvar
            </Button>
          </FooterRight>
        </Footer>
      }
    >
      <Body>
        <Info>
          Este controle é visível apenas para o time interno. Não aparece para o
          seller e não altera saldo disponível, saque, recebíveis ou reservas.
        </Info>

        {existente && existente.valorPago > 0 && (
          <ResetWarning data-testid="ajuste-antecipacao-reset-warning">
            ⚠️ Este ajuste já tem{' '}
            <strong>
              {formatValorMoeda(existente.valorPago, existente.moedaSigla)}
            </strong>{' '}
            quitado por vendas. Editar o valor agora vai resetar o tracking — o
            novo total cadastrado vira o novo baseline.
          </ResetWarning>
        )}

        {errorMessage && <ErrorAlert>{errorMessage}</ErrorAlert>}

        <Field>
          <FieldLabel htmlFor="ajuste-moeda">Moeda *</FieldLabel>
          <Select
            id="ajuste-moeda"
            data-testid="ajuste-antecipacao-moeda"
            value={String(moeda)}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setMoeda(Number(e.target.value) as MoedaAjuste)
            }
            disabled={Boolean(existente)}
            options={MOEDA_AJUSTE_ORDER.map((m) => ({
              value: String(m),
              label: MOEDA_AJUSTE_LABEL[m],
            }))}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="ajuste-valor">Valor *</FieldLabel>
          <Input
            id="ajuste-valor"
            data-testid="ajuste-antecipacao-valor"
            type="number"
            step="0.01"
            min={0}
            value={valor}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setValor(e.target.value)
            }
            hasError={Boolean(errors.valor)}
          />
          {errors.valor && <FieldError>{errors.valor}</FieldError>}
        </Field>

        <Field>
          <FieldLabel htmlFor="ajuste-motivo">Observação</FieldLabel>
          <Textarea
            id="ajuste-motivo"
            data-testid="ajuste-antecipacao-motivo"
            rows={3}
            maxLength={1000}
            placeholder="Ex.: incidente de antecipação reportado pelo time financeiro em DD/MM/YYYY"
            value={motivo}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setMotivo(e.target.value)
            }
          />
        </Field>
      </Body>
    </Modal>
  );
};
